import React, { useState, useEffect } from 'react';
import { X, AlertTriangle, Send, Check, User, AlertCircle, Search } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { filterProfanity } from '../utils/contentFilter';
import { useAuth } from '../context/AuthContext';
import { getAllUsers } from '../services/userService';
import { User as UserType } from '../types';

interface ReportFormProps {
  isOpen: boolean;
  onClose: () => void;
  reportType: 'user' | 'content' | 'technical' | 'other';
  userId?: string; // Optional: pre-selected user ID if reporting from a user profile
}

const ReportForm: React.FC<ReportFormProps> = ({ isOpen, onClose, reportType, userId }) => {
  const { currentUser } = useAuth();
  const [reportReason, setReportReason] = useState('');
  const [reportDescription, setReportDescription] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState<UserType | null>(null);
  const [users, setUsers] = useState<UserType[]>([]);
  const [showUserSearch, setShowUserSearch] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (reportType === 'user') {
      // Load all users for the search dropdown
      const loadUsers = async () => {
        try {
          const allUsers = await getAllUsers();
          const filteredUsers = allUsers.filter(user => user.id !== currentUser?.id);
          setUsers(filteredUsers);

          // If userId is provided, pre-select that user
          if (userId) {
            const user = filteredUsers.find(u => u.id === userId);
            if (user) {
              setSelectedUser(user);
            }
          }
        } catch (error) {
          console.error('Failed to load users:', error);
          setError('Couldn\'t load user list right now');
        }
      };

      loadUsers();
    }
  }, [reportType, userId, currentUser?.id]);

  const filteredUsers = users.filter(user => 
    user.username.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getReportTypeTitle = () => {
    switch (reportType) {
      case 'user':
        return 'Report a User';
      case 'content':
        return 'Report Content';
      case 'technical':
        return 'Report Technical Issue';
      case 'other':
        return 'Report Other Concern';
    }
  };

  const getReasonOptions = () => {
    switch (reportType) {
      case 'user':
        return [
          { value: 'harassment', label: 'Harassment or Bullying' },
          { value: 'inappropriate_behavior', label: 'Inappropriate Behavior' },
          { value: 'fake_profile', label: 'Fake Profile' },
          { value: 'scam', label: 'Scam or Fraud' },
          { value: 'discrimination', label: 'Discrimination' },
          { value: 'threats', label: 'Threats or Violence' },
          { value: 'other', label: 'Other' }
        ];
      case 'content':
        return [
          { value: 'inappropriate', label: 'Inappropriate Content' },
          { value: 'misleading', label: 'Misleading Information' },
          { value: 'offensive', label: 'Offensive Language' },
          { value: 'spam', label: 'Spam or Advertising' },
          { value: 'other', label: 'Other' }
        ];
      case 'technical':
        return [
          { value: 'bug', label: 'Bug or Error' },
          { value: 'performance', label: 'Performance Issue' },
          { value: 'payment', label: 'Payment Problem' },
          { value: 'feature', label: 'Feature Not Working' },
          { value: 'other', label: 'Other' }
        ];
      case 'other':
        return [
          { value: 'suggestion', label: 'Suggestion' },
          { value: 'question', label: 'Question' },
          { value: 'feedback', label: 'General Feedback' },
          { value: 'other', label: 'Other' }
        ];
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (reportType === 'user' && !selectedUser) {
      setError('Please select a user to report');
      return;
    }
    
    if (!reportReason) {
      setError('Please select a reason for your report');
      return;
    }
    
    if (!reportDescription.trim()) {
      setError('Please provide details about the issue');
      return;
    }
    
    setIsSubmitting(true);
    setError('');
    
    try {
      // Filter profanity from description
      const filteredDescription = filterProfanity(reportDescription);

      // Prepare report data
      const reportData = {
        type: reportType,
        reason: reportReason,
        description: filteredDescription,
        reportedUserId: selectedUser?.id,
        reporterId: currentUser?.id || 'anonymous',
        createdAt: new Date().toISOString()
      };

      // Send report to the server
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/submit-report`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`
        },
        body: JSON.stringify(reportData)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        console.error('Report submission error:', errorData);
        throw new Error(errorData.error || 'Failed to submit report');
      }

      setSuccess(true);

      // Reset form after success
      setReportReason('');
      setReportDescription('');
      setSelectedUser(null);
      setSearchTerm('');

      // Close the form after showing success message
      setTimeout(() => {
        onClose();
        setSuccess(false);
      }, 3000);
    } catch (err: any) {
      console.error('Report submission failed:', err);
      setError(err.message || 'Couldn\'t submit report — try again in a moment.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="bg-theme-cards rounded-2xl shadow-card w-full max-w-md border border-theme-cards"
      >
        <div className="p-4 border-b border-primary-200 flex justify-between items-center">
          <h2 className="text-xl font-semibold text-contrast flex items-center">
            <AlertTriangle size={20} className="mr-2 text-secondary" />
            {getReportTypeTitle()}
          </h2>
          <button
            onClick={onClose}
            className="text-contrast-500 hover:text-contrast p-1 rounded-full"
          >
            <X size={20} />
          </button>
        </div>

        <div className="p-6">
          {success ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center py-6"
            >
              <div className="w-16 h-16 bg-accent2/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check size={32} className="text-accent2" />
              </div>
              <h3 className="text-xl font-semibold mb-2 text-contrast">Report Submitted</h3>
              <p className="text-contrast-400">
                Thank you for your report. Our team will review it and take appropriate action.
              </p>
            </motion.div>
          ) : (
            <form onSubmit={handleSubmit}>
              {error && (
                <div className="mb-4 bg-secondary/10 p-3 rounded-md flex items-start">
                  <AlertCircle size={18} className="mr-2 mt-0.5 text-secondary flex-shrink-0" />
                  <p className="text-secondary">{error}</p>
                </div>
              )}

              {reportType === 'user' && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-contrast mb-1">
                    Tribe Member to Report*
                  </label>
                  
                  {selectedUser ? (
                    <div className="flex items-center justify-between p-3 bg-primary-100 border border-primary-200 rounded-md">
                      <div className="flex items-center">
                        <div className="w-8 h-8 bg-primary-200 rounded-full flex items-center justify-center mr-2">
                          <User size={16} className="text-contrast" />
                        </div>
                        <span className="text-contrast">{selectedUser.username}</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => setSelectedUser(null)}
                        className="text-contrast-500 hover:text-contrast"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  ) : (
                    <div className="relative">
                      <div className="flex">
                        <div className="relative flex-grow">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <Search size={16} className="text-contrast-500" />
                          </div>
                          <input
                            type="text"
                            value={searchTerm}
                            onChange={(e) => {
                              setSearchTerm(e.target.value);
                              setShowUserSearch(true);
                            }}
                            onFocus={() => setShowUserSearch(true)}
                            className="w-full pl-10 p-3 bg-primary-100 border border-primary-200 rounded-md focus:ring-2 focus:ring-secondary focus:border-secondary text-contrast"
                           placeholder="Search for a Tribe Member..."
                          />
                        </div>
                      </div>
                      
                      <AnimatePresence>
                        {showUserSearch && searchTerm && (
                          <motion.div
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            className="absolute z-10 mt-1 w-full bg-primary-100 rounded-md shadow-md max-h-60 overflow-y-auto border border-primary-200"
                          >
                            {filteredUsers.length > 0 ? (
                              <ul>
                                {filteredUsers.map(user => (
                                  <li
                                    key={user.id}
                                    className="px-4 py-2 hover:bg-primary-200 cursor-pointer text-contrast"
                                    onClick={() => {
                                      setSelectedUser(user);
                                      setSearchTerm('');
                                      setShowUserSearch(false);
                                    }}
                                  >
                                    <div className="flex items-center">
                                      <div className="w-6 h-6 bg-primary-200 rounded-full flex items-center justify-center mr-2">
                                        <User size={12} className="text-contrast" />
                                      </div>
                                      {user.username}
                                    </div>
                                  </li>
                                ))}
                              </ul>
                            ) : (
                              <div className="px-4 py-2 text-contrast-500">No Tribe Members found</div>
                            )}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  )}
                </div>
              )}

              <div className="mb-4">
                <label htmlFor="reportReason" className="block text-sm font-medium text-contrast mb-1">
                  Reason*
                </label>
                <select
                  id="reportReason"
                  value={reportReason}
                  onChange={(e) => setReportReason(e.target.value)}
                  className="w-full p-3 bg-primary-100 border border-primary-200 rounded-md focus:ring-2 focus:ring-secondary focus:border-secondary text-contrast"
                  required
                >
                  <option value="">Select a reason</option>
                  {getReasonOptions().map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="mb-6">
                <label htmlFor="reportDescription" className="block text-sm font-medium text-contrast mb-1">
                  Details*
                </label>
                <textarea
                  id="reportDescription"
                  value={reportDescription}
                  onChange={(e) => setReportDescription(e.target.value)}
                  rows={5}
                  className="w-full p-3 bg-primary-100 border border-primary-200 rounded-md focus:ring-2 focus:ring-secondary focus:border-secondary text-contrast"
                  placeholder="Please provide as much detail as possible..."
                  required
                ></textarea>
                <p className="text-xs text-contrast-500 mt-1">
                  Please include specific details, dates, and any relevant information that will help us investigate.
                </p>
              </div>

              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={onClose}
                  className="mr-3 px-4 py-2 border border-primary-200 rounded-md hover:bg-primary-100 text-contrast"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="bg-theme-primary text-white px-4 py-2 rounded-md hover:bg-theme-primary-darker transition-colors flex items-center disabled:opacity-50"
                >
                  {isSubmitting ? (
                    <>
                      <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                      Submitting...
                    </>
                  ) : (
                    <>
                      <Send size={18} className="mr-2" />
                      Submit Report
                    </>
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default ReportForm;